/**
 * Database Client - Native Versie (SQLite)
 * Regelt de verbinding met de telefoon database (iOS/Android)
 * Want op mobiel doen we moeilijk met echte databases
 */

import SQLite, {
  SQLiteDatabase,
  enablePromise,
  ResultSet,
} from 'react-native-sqlite-storage';
import { ProductDatabaseRow } from '../models';
import { maakApplicatieFout, bepaalDatabaseFoutType } from '../models/AppError';
import { DatabaseService } from './DatabaseService';

// Promises aanzetten want die call back hell hoeven we niet
enablePromise(true);

// Database instellingen
const DATABASE_NAME = 'beauty.db';
const DATABASE_LOCATION = 'default';
const MAX_RETRY_ATTEMPTS = 3; // Drie keer is scheepsrecht
const RETRY_DELAY_MS = 1000; // Even ademhalen tussendoor

class DatabaseClientNative implements DatabaseService {
  private db: SQLiteDatabase | null = null;
  private initialized = false;

  /**
   * Database verbinding starten (blijven proberen als het faalt)
   */
  async initialize(): Promise<void> {
    if (this.initialized && this.db) {
      return;
    }

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        // Connectie openen.
        // We gebruiken createFromLocation om de db te kopiëren uit de assets als ie er nog niet is.
        this.db = await SQLite.openDatabase({
          name: DATABASE_NAME,
          location: DATABASE_LOCATION,
          createFromLocation: '~beauty.db', // Database die we al hebben gemaakt
        });

        // Even checken of ie het doet
        await this.verifyDatabase();

        // Index maken op Barcode zodat we snel vinden (anders duurt het eeuwen)
        await this.createIndexes();

        this.initialized = true;
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Onbekende fout');
        console.error(`Database start poging ${attempt} mislukt:`, error);

        // Rotzooi opruimen
        if (this.db) {
          try {
            await this.db.close();
          } catch (closeError) {
            console.error('Fout bij sluiten database na mislukte start:', closeError);
          }
          this.db = null;
        }

        // Even wachten en opnieuw proberen
        if (attempt < MAX_RETRY_ATTEMPTS) {
          await this.delay(RETRY_DELAY_MS * attempt);
        }
      }
    }

    // Alles geprobeerd, niks gelukt
    const errorType = bepaalDatabaseFoutType(lastError);
    throw maakApplicatieFout(
      errorType,
      `Kon database niet starten na ${MAX_RETRY_ATTEMPTS} pogingen: ${lastError?.message || 'Onbekende fout'}`,
      true,
      () => this.initialize(),
      lastError || undefined,
    );
  }

  /**
   * Is de database er al?
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Testen of we bij de data kunnen
   */
  private async verifyDatabase(): Promise<void> {
    if (!this.db) {
      throw new Error('Database niet gestart');
    }

    // Simpel testje - gewoon tellen hoeveel producten er zijn
    await this.db.executeSql('SELECT COUNT(*) as count FROM products');
  }

  /**
   * Hulpfunctie om even te wachten (sleep)
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Indexen maken voor snelheid
   */
  private async createIndexes(): Promise<void> {
    if (!this.db) {
      throw new Error('Database niet gestart');
    }

    try {
      // Index op Barcode kolom maken.
      // Dit is SUPER belangrijk voor de performance.
      // Zonder index moet hij 10.000 rijen doorzoeken, met index weet ie direct waar het staat.
      await this.db.executeSql(
        'CREATE INDEX IF NOT EXISTS idx_barcode ON products(Barcode)',
      );
    } catch (error) {
      console.error('Kon indexen niet maken:', error);
      // Niet erg als dit faalt, is maar optimalisatie
    }
  }

  /**
   * Database instantie ophalen
   */
  private getDatabase(): SQLiteDatabase {
    if (!this.db || !this.initialized) {
      const error = maakApplicatieFout(
        'database_connection',
        'Database niet gestart. Roep eerst initialize() aan.',
        true,
        () => this.initialize(),
      );
      throw error;
    }
    return this.db;
  }

  /**
   * Query uitvoeren met retry logica (voor als ie even hikt)
   */
  private async executeQuery<T = any>(
    query: string,
    params: any[] = [],
    retryCount = 0,
  ): Promise<T[]> {
    try {
      const db = this.getDatabase();
      const [results]: [ResultSet] = await db.executeSql(query, params);
      const rows: T[] = [];

      for (let i = 0; i < results.rows.length; i++) {
        rows.push(results.rows.item(i));
      }

      return rows;
    } catch (error) {
      console.error('Query uitvoeren mislukt:', error);

      // Opnieuw proberen als het vaag doet
      if (retryCount < 2) {
        await this.delay(500);
        return this.executeQuery<T>(query, params, retryCount + 1);
      }

      // Determine specific error type
      const errorType = bepaalDatabaseFoutType(error);
      const appError = maakApplicatieFout(
        errorType,
        `Query mislukt: ${error instanceof Error ? error.message : 'Onbekende fout'}`,
        true,
        () => this.executeQuery<T>(query, params, 0),
        error instanceof Error ? error : undefined,
      );
      throw appError;
    }
  }

  /**
   * Zoek op ID
   */
  async getProductById(id: number): Promise<ProductDatabaseRow | null> {
    try {
      const query = 'SELECT * FROM products WHERE Nummer = ? LIMIT 1';
      const results = await this.executeQuery<ProductDatabaseRow>(query, [id]);
      return results.length > 0 ? results[0] : null;
    } catch (error) {
      console.error(`Fout bij ophalen product op ID ${id}:`, error);
      throw error; // Re-throw AppError from executeQuery
    }
  }

  /**
   * Zoek op barcode
   */
  async getProductByBarcode(
    barcode: string,
  ): Promise<ProductDatabaseRow | null> {
    try {
      const query = 'SELECT * FROM products WHERE Barcode = ? LIMIT 1';
      const results = await this.executeQuery<ProductDatabaseRow>(query, [
        barcode,
      ]);
      return results.length > 0 ? results[0] : null;
    } catch (error) {
      console.error(`Fout bij ophalen product op barcode ${barcode}:`, error);
      throw error; // Re-throw AppError from executeQuery
    }
  }

  /**
   * Zoeken op naam of merk
   */
  async searchProducts(
    searchTerm: string,
  ): Promise<ProductDatabaseRow[]> {
    try {
      const query = `
        SELECT * FROM products 
        WHERE Product LIKE ? OR Merk LIKE ?
        ORDER BY Product ASC
        LIMIT 50
      `;
      const searchPattern = `%${searchTerm}%`;
      return this.executeQuery<ProductDatabaseRow>(query, [
        searchPattern,
        searchPattern,
      ]);
    } catch (error) {
      console.error(`Fout bij zoeken producten met term "${searchTerm}":`, error);
      throw error; // Re-throw AppError from executeQuery
    }
  }

  /**
   * Alles van een categorie ophalen
   */
  async getProductsByCategory(
    category: string,
  ): Promise<ProductDatabaseRow[]> {
    try {
      const query = 'SELECT * FROM products WHERE Categorie = ? ORDER BY Product ASC';
      return this.executeQuery<ProductDatabaseRow>(query, [category]);
    } catch (error) {
      console.error(`Fout bij ophalen producten in categorie "${category}":`, error);
      throw error; // Re-throw AppError from executeQuery
    }
  }

  /**
   * Willekeurige producten ophalen (voor discovery)
   */
  async getRandomProducts(limit: number): Promise<ProductDatabaseRow[]> {
    try {
      // SQLite kan dit heel snel met RANDOM()
      const query = 'SELECT * FROM products ORDER BY RANDOM() LIMIT ?';
      return this.executeQuery<ProductDatabaseRow>(query, [limit]);
    } catch (error) {
      console.error(`Fout bij ophalen willekeurige producten (limiet ${limit}):`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const databaseClient = new DatabaseClientNative();
